import type { DocumentData, DocumentReference } from 'firebase/firestore'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { MobxDocument } from './document'
import type { ObservableModel, ObservableModelClass } from './model'
import type { StaticModelCollection } from './model'

/**
 * Convert raw { id, data } objects from an API response into observable model instances.
 * Used when fetching data via HTTPS callable functions instead of direct Firestore reads.
 *
 * The resulting models are read-only (ref is null) since they weren't fetched from Firestore.
 */
export function convertApiDocsToModels<
  T extends DocumentData,
  M extends ObservableModel<T>,
>(
  repository: FirebaseRepository,
  // `data` is a loose bag here (API responses come back as { id, data } with
  // Firestore-shaped but untyped fields). It's cast to T and stored on the model,
  // which reads straight from `doc.data` — so callers are responsible for the
  // data already matching the schema (the server maps/zod-parses it first).
  docs: { id: string; data: DocumentData }[],
  modelClass: ObservableModelClass<T, M>
): M[] {
  return docs.map((doc) => {
    const mobxDoc: MobxDocument<T> = {
      id: doc.id,
      data: doc.data as T,
      ref: null as unknown as DocumentReference,
      fromCache: false,
      hasPendingWrites: false,
      exists: true,
    }
    return new modelClass(repository, mobxDoc)
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SubcollectionMapping = {
  key: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modelClass: ObservableModelClass<any, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  target: StaticModelCollection<any>
}

/**
 * Populate multiple model collections from an API response in one pass.
 * Each entry maps a response key to a model class and target collection.
 */
export function populateModelsFromApiResponse(
  repository: FirebaseRepository,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: Record<string, any>,
  mappings: SubcollectionMapping[]
) {
  for (const { key, modelClass, target } of mappings) {
    const docs = response[key]
    if (docs) {
      target.replaceModels(convertApiDocsToModels(repository, docs, modelClass))
    }
  }
}
